import React, { useEffect } from 'react';

import { CircularProgress } from '@vk-hr-tek/ui/CircularProgress';
import { Box } from '@vk-hr-tek/ui/Box';
import { HTTP_INTERNAL_SERVER_ERROR_STATUS } from '@vk-hr-tek/core/http/codes';

import Can from '@app/ability/Can';
import { AppAbilities } from '@app/ability/ability';
import { useDispatch, useSelector } from '@app/hooks';

import { useAuth } from '../../auth';
import {
  selectStatus,
  getUser,
  getCandidateUser,
  selectError,
} from '../../user';
import { selectCandidateUserStatus } from '../../user/slice';
import { PageError } from '../page';
import { useApplication } from '../../auth/hooks/useApplication';
import { AbilityProvider } from '../AbilityProvider';

interface RequireAuthProps {
  children: React.ReactNode;
  resource: AppAbilities[1];
  role?: 'employee' | 'company' | 'general';
}

export const RequireAuth = ({ children, resource, role }: RequireAuthProps) => {
  const dispatch = useDispatch();

  const isAuthenticated = useAuth();
  const appType = useApplication();
  const status = useSelector(
    appType === 'candidateApp' ? selectCandidateUserStatus : selectStatus,
  );
  const error = useSelector(selectError);

  useEffect(() => {
    if (isAuthenticated && status === 'idle') {
      dispatch(appType === 'candidateApp' ? getCandidateUser() : getUser());
    }
  }, [dispatch, isAuthenticated, status, appType]);

  if (!isAuthenticated) {
    return null;
  }

  if (status === 'idle' || status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" pt="40">
        <CircularProgress size={50} />
      </Box>
    );
  }

  if (status === 'failed') {
    return (
      <PageError
        // eslint-disable-next-line no-magic-numbers
        status={error?.status || HTTP_INTERNAL_SERVER_ERROR_STATUS}
        traceId={error?.traceId}
        errorMessage="Произошла ошибка получения данных пользователя"
      />
    );
  }

  if (!role) {
    return (
      <>
        <Can do="read" on={resource}>
          {children}
        </Can>
        <Can not do="read" on={resource}>
          <PageError status={403} />
        </Can>
      </>
    );
  } else {
    return (
      <AbilityProvider role={role}>
        <Can do="read" on={resource}>
          {children}
        </Can>
        <Can not do="read" on={resource}>
          <PageError status={403} />
        </Can>
      </AbilityProvider>
    );
  }
};
